import { UnauthApi } from '@/api';
import { PublicClient } from '@/api/publicClient';
import type { WEBVIEW_TYPE_ENTRY } from '@/constants/entry';

export const GOODS_LOW_STOCK_PERCENT = 10;

//* 잔여·마감은 바로 보여야 해서 캐시하지 않는다
const fetchYearEndGoodsRegions = async (entry: WEBVIEW_TYPE_ENTRY) => {
  const response = await UnauthApi[
    'get:/wello/v2/allows/hometown-edition/regions'
  ]({ client: PublicClient, query: { entry } });

  return response.data?.context ?? null;
};

type YearEndGoodsRegion = NonNullable<
  Awaited<ReturnType<typeof fetchYearEndGoodsRegions>>
>['regions'][number];

const toGoodsInstitution = (region: YearEndGoodsRegion) => ({
  institutionId: region.homeInstitutionId,
  institutionRouteId: region.institutionSlugId ?? region.homeInstitutionId,
  institutionName: region.institutionName,
  institutionLogoUrl: region.logoImageUrl ?? null,
  sidoName: region.sidoName ?? null,
  remainingQuantity: region.remainingCount,
  totalQuantity: region.totalCount,
  status: region.status,
  giftProduct: region.giftProduct ?? null,
});

export type YearEndGoodsInstitution = ReturnType<typeof toGoodsInstitution>;

//* 실패하면 목으로 채우지 않고 연말 에디션을 숨긴다 — 없는 재고를 약속하지 않기 위해
export const getYearEndGoodsRegions = async (entry: WEBVIEW_TYPE_ENTRY) => {
  const context = await fetchYearEndGoodsRegions(entry).catch(() => null);

  return {
    periodActive: context?.periodActive ?? false,
    allClosed: context?.allClosed ?? false,
    institutions: (context?.regions ?? []).map(toGoodsInstitution),
  };
};

export const getGoodsStockPercent = ({
  remainingQuantity,
  totalQuantity,
}: YearEndGoodsInstitution) => {
  if (totalQuantity <= 0 || remainingQuantity <= 0) {
    return 0;
  }

  //* 남은 재고가 있는데 「재고 0%」로 보이면 마감과 구분되지 않는다
  return Math.max(1, Math.floor((remainingQuantity / totalQuantity) * 100));
};

export const isGoodsSoldOut = (institution: YearEndGoodsInstitution) => {
  return institution.status === 'CLOSED';
};

export const isGoodsLowStock = (institution: YearEndGoodsInstitution) => {
  return (
    !isGoodsSoldOut(institution) &&
    getGoodsStockPercent(institution) <= GOODS_LOW_STOCK_PERCENT
  );
};

//* 잔여 %·마감 사유·최소 기부 금액은 서버 계산값이 기준이다 — 마감 판정은 캐시 없이 매번 받는다
export const getYearEndGoodsRegionEdition = async (
  homeInstitutionId: number,
  entry: WEBVIEW_TYPE_ENTRY,
) => {
  const response = await UnauthApi[
    'get:/wello/v2/allows/hometown-edition/regions/{homeInstitutionId}'
  ]({
    client: PublicClient,
    path: { homeInstitutionId },
    query: { entry },
  }).catch(() => null);

  return response?.data?.context?.edition ?? null;
};

export type YearEndGoodsRegionEdition = NonNullable<
  Awaited<ReturnType<typeof getYearEndGoodsRegionEdition>>
>;
